import { ErrorMessage, Form, Formik } from "formik";
import { CaretLeft } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import * as yup from "yup";
import BankListInput from "../../../../components/BankListInput";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Bank as TBank } from "../../../../@types";
import { ResolvedAccount } from "../../../../hooks/useResolveBankAccount";
import { useSendContext } from "../../../../context/SendContext";
import useBankList from "../../../../hooks/useBankList";
import { HashLoader } from "react-spinners";
import { useAuth } from "../../../../context/AuthContext";
import CreatePinModal from "../../../../components/CreatePinModal";
import useResolveBankAccount from "../../../../hooks/useResolveBankAccount";
import { getBankLogo } from "../../../../helpers/logos";

const SCHEMA = yup.object().shape({
  bank: yup.object().required("Please kindly select recipient's bank"),
  accountNumber: yup
    .string()
    .trim()
    .matches(/^\d{10}$/, "Account number must be exactly 10 digits")
    .required("Please kindly enter recipient's account number"),
});

const AccountDetails = () => {
  const navigate = useNavigate();
  const { setTransfer, transfer } = useSendContext();
  const { user } = useAuth();
  const [showTransactionPinModal, setShowTransactionPinModal] = useState(false);
  const { bankListData, isLoading } = useBankList();

  const [reciepient, setReciepient] = useState<{
    accountNumber: string;
    bank: TBank;
  } | null>(null);

  const [foundUser, setFoundUser] = useState<ResolvedAccount | null>(null);

  const foundBank = useMemo(() => {
    if (!reciepient) return undefined;
    return bankListData?.data?.find((bank) => bank?.code === reciepient.bank.code);
  }, [reciepient, bankListData]);

  const {
    verifyBankDetails,
    verifyingBankDetails,
    isVerifyingBankDetailsError,
    verifyingBankDetailsError,
  } = useResolveBankAccount({
    onSuccess(data) {
      if (data.data) {
        setFoundUser(data.data);
      }
    },
  });

  useEffect(() => {
    if (reciepient) {
      verifyBankDetails({
        account_number: reciepient.accountNumber,
        bank_code: reciepient.bank.code,
      });
    }
  }, [reciepient, verifyBankDetails]);

  const handleSend = useCallback(() => {
    if (foundUser && reciepient) {
      if (!user?.pin_status) {
        setShowTransactionPinModal(true);
      } else {
        setTransfer({
          accountNumber: reciepient.accountNumber,
          bank: {
            ...reciepient.bank,
          },
          transferType: "inter",
          recipientName: foundUser.account_name,
        });
        navigate("amount");
      }
    }
  }, [foundUser, reciepient, setTransfer, navigate, user?.pin_status]);

  return (
    <div>
      {verifyingBankDetails && (
        <div className="fixed top-0 z-[99] left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}

      <Formik
        initialValues={{
          bank: transfer?.bank,
          accountNumber: transfer?.accountNumber || "",
        }}
        validationSchema={SCHEMA}
        onSubmit={({ accountNumber, bank }) => {
          if (bank) {
            setReciepient({
              accountNumber,
              bank: bank,
            });
          }
        }}
      >
        {({
          values,
          handleSubmit,
          isValid,
          dirty,
          setFieldValue,
          handleChange,
          handleBlur,
        }) => {
          return (
            <Form className="flex items-center justify-center gap-10 py-20 text-center flex-col px-5">
              <div className="flex flex-col lg:gap-2">
                <div className="font-semibold text-2xl lg:text-3xl">
                  Send money to a Bank account
                </div>
                <div className="text-gray-500">
                  Enter the recipient's bank and account number to verify their
                  details before sending money.
                </div>
              </div>
              <div className="col-span-full bg-white drop-shadow-sm p-5 rounded-xl flex flex-col gap-5 max-w-3xl w-full">
                <div className="flex flex-col gap-8">
                  <div className="flex flex-col gap-2 w-full text-left">
                    <label htmlFor="bank" className="text-sm font-medium">
                      Select Bank
                    </label>
                    <BankListInput
                      bankListData={bankListData?.data}
                      isLoading={isLoading}
                      selected={values.bank?.code}
                      onSelect={(bank) => {
                        setFoundUser(null);
                        setFieldValue("bank", bank);
                      }}
                    />
                    <ErrorMessage
                      name="bank"
                      component="div"
                      className="text-red-500"
                    />
                  </div>
                  <div className="flex flex-col gap-2 w-full text-left">
                    <label
                      htmlFor="accountNumber"
                      className="text-sm font-medium"
                    >
                      Bank account number
                    </label>
                    <input
                      onChange={(e) => {
                        setFoundUser(null);
                        handleChange("accountNumber")(e);
                      }}
                      onBlur={handleBlur("accountNumber")}
                      type="text"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="Enter 10 digit account number"
                      id="accountNumber"
                      name="accountNumber"
                      className="rounded-lg border border-gray-300 shadow px-4 py-3 w-full"
                    />
                    <ErrorMessage
                      name="accountNumber"
                      component="div"
                      className="text-red-500"
                    />
                  </div>
                  {foundUser && (
                    <motion.div
                      initial={{
                        opacity: 0,
                      }}
                      animate={{
                        opacity: 1,
                      }}
                      className="flex flex-col w-full gap-2 text-left"
                    >
                      <div className="bg-gray-50 p-4 rounded-lg flex gap-4 items-center">
                        <div className="w-8 h-8 rounded-full">
                          <img
                            src={getBankLogo(foundBank)}
                            alt={foundBank?.name}
                            className="w-full h-full object-contain rounded-md"
                          />
                        </div>
                        <div className="flex-1">
                          <div className="text-lg font-medium">
                            {foundUser.account_name}
                          </div>
                          <div className="text-sm text-gray-700">
                            {foundBank?.name}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
                {isVerifyingBankDetailsError && (
                  <div className="text-red-500 text-left">
                    {verifyingBankDetailsError?.message}
                  </div>
                )}
                <div className="flex justify-between w-full max-w-3xl">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300"
                  >
                    <CaretLeft weight="bold" size={20} />
                    <span>Back</span>
                  </button>
                  <button
                    type="button"
                    disabled={verifyingBankDetails || !isValid || !dirty}
                    onClick={() => (foundUser ? handleSend() : handleSubmit())}
                    className="p-2 py-3 text-center bg-deepBlue text-white px-10 border-2 border-deepBlue rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {foundUser
                      ? "Proceed to Send"
                      : verifyingBankDetails
                      ? "Checking....."
                      : "Confirm"}
                  </button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
      {showTransactionPinModal && (
        <CreatePinModal setShowPinModal={setShowTransactionPinModal} />
      )}
    </div>
  );
};

export default AccountDetails;
